<!-- Backdrop — only visible in expanded (dialog) mode. Stays mounted so
     OnPush + ngIf timing can't make it skip a render. Clicking it pops
     the user back to drawer mode (does not close). -->
<div
    class="gable-backdrop"
    [class.is-visible]="isOpen && isExpanded"
    (click)="onClose()"
    aria-hidden="true"></div>

<div
    class="gable-drawer"
    [class.is-open]="isOpen"
    [class.is-expanded]="isExpanded"
    [ngStyle]="drawerStyle"
    role="dialog"
    aria-label="Gable AI Assistant">

    <!-- Resize handle on left edge — drawer mode only; expanded mode has
         fixed viewport-relative size so resize doesn't apply. -->
    <span
        *ngIf="!isExpanded"
        class="gable-resize-handle"
        appGableDrawerResize
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize drawer"></span>

    <!-- ============================================================ -->
    <!-- HEADER                                                       -->
    <!-- ============================================================ -->
    <header class="gable-header">
        <!-- Back arrow surfaces only when an inline secondary view (e.g.
             Teach Gable) is showing in the drawer body — clicking it
             swaps back to the chat. Circular V2 chrome to match the
             other header affordances. -->
        <button
            *ngIf="drawerView === 'teach'"
            type="button"
            class="gable-back-btn"
            (click)="onTeachClosed(null)"
            aria-label="Back to chat">
            <i class="ph-bold ph-arrow-left icon-muted icon-16" aria-hidden="true"></i>
        </button>
        <div class="gable-title-col">
            <h2 class="gable-title">
                <ng-container *ngIf="drawerView === 'teach'; else chatTitle">
                    <span>Teach Gable</span>
                    <app-super-star></app-super-star>
                </ng-container>
                <ng-template #chatTitle>
                    <!-- SD-6742: in drawer mode the title doubles as a history
                         picker — a caret reveals a menu of recent + archived
                         chats. Expanded mode uses the left sidebar instead, so
                         the title stays a plain label there. -->
                    <ng-container *ngIf="!isExpanded; else plainTitle">
                        <button
                            type="button"
                            class="gable-title-trigger"
                            [matMenuTriggerFor]="historyMenu"
                            (menuOpened)="onHistoryMenuOpened()"
                            aria-label="Chat history">
                            <span class="gable-title-text">{{ chatHeaderTitle }}</span>
                            <i class="ph-bold ph-caret-down gable-title-caret icon-muted icon-12" aria-hidden="true"></i>
                        </button>
                    </ng-container>
                    <ng-template #plainTitle>
                        <span class="gable-title-text">{{ chatHeaderTitle }}</span>
                    </ng-template>
                </ng-template>
            </h2>
            <!-- Live-state status line under the title (SD-7969: real
                 pending-proposal count). Deep-links to the Activity tab. -->
            <button
                *ngIf="autopilotUi && drawerView !== 'teach'"
                type="button"
                class="gable-status-line"
                (click)="onSelectTab('activity')"
                aria-label="Open Gable activity">
                <span class="gable-status-dot" aria-hidden="true"></span>
                <span>{{ pendingProposals.length > 0 ? pendingProposals.length + ' suggestion' + (pendingProposals.length === 1 ? '' : 's') + ' need review' : 'Up to date' }}</span>
            </button>
        </div>
        <flex-spacer></flex-spacer>
        <!-- SD-6742: quick new-chat affordance in the header (chat view only). -->
        <button
            *ngIf="drawerView !== 'teach'"
            type="button"
            class="gable-header-btn"
            (click)="onNewChat()"
            aria-label="Start new chat">
            <i class="ph-bold ph-plus icon-muted icon-16" aria-hidden="true"></i>
        </button>
        <button
            type="button"
            class="gable-header-btn"
            (click)="onToggleDock()"
            [attr.aria-label]="isExpanded ? 'Collapse to drawer' : 'Expand to dialog'">
            <i
                class="ph-bold icon-muted icon-16"
                [class.ph-arrows-out-simple]="!isExpanded"
                [class.ph-arrows-in-simple]="isExpanded"
                aria-hidden="true"></i>
        </button>
        <button
            type="button"
            class="gable-header-btn"
            (click)="onClose()"
            aria-label="Close drawer">
            <i class="ph-bold ph-x icon-muted icon-16" aria-hidden="true"></i>
        </button>
    </header>

    <!-- ============================================================ -->
    <!-- View tabs — Chat + Activity (both live, SD-7969). Kill        -->
    <!-- switch: localStorage 'gable-autopilot-ui' = 'off'.            -->
    <!-- ============================================================ -->
    <nav
        *ngIf="autopilotUi && drawerView !== 'teach'"
        class="gable-vtabs"
        role="tablist"
        aria-label="Gable views">
        <button
            type="button"
            class="gable-vtab"
            role="tab"
            [attr.aria-selected]="activeTab === 'chat'"
            [class.active]="activeTab === 'chat'"
            (click)="onSelectTab('chat')">
            <i class="ph-bold ph-chat-circle icon-16" aria-hidden="true"></i>
            <span>Chat</span>
        </button>
        <!-- No Autopilot settings tab (CEO decision 2026-07-11): Autopilot is
             suggestions-only — Chat + Activity are the whole surface. -->
        <button
            type="button"
            class="gable-vtab"
            role="tab"
            [attr.aria-selected]="activeTab === 'activity'"
            [class.active]="activeTab === 'activity'"
            (click)="onSelectTab('activity')">
            <i class="ph-bold ph-clock-countdown icon-16" aria-hidden="true"></i>
            <span>Activity</span>
            <span class="gable-vtab-count" *ngIf="pendingProposals.length > 0">{{ pendingProposals.length }}</span>
        </button>
    </nav>

    <!-- ============================================================ -->
    <!-- SD-6742 — drawer-mode history menu (off the title caret)     -->
    <!-- ============================================================ -->
    <mat-menu #historyMenu="matMenu" xPosition="before" class="gable-menu-panel gable-history-menu">
        <button mat-menu-item (click)="onNewChat()">
            <i class="ph-bold ph-plus icon-muted icon-16" aria-hidden="true"></i>
            <span>New chat</span>
        </button>

        <mat-divider></mat-divider>

        <div class="gable-history-menu-label">
            <i class="ph-bold ph-clock-counter-clockwise icon-muted icon-14" aria-hidden="true"></i>
            <span>Recent chats</span>
        </div>

        <ng-container *ngIf="recentMenuConversations.length > 0; else noRecent">
            <button
                mat-menu-item
                *ngFor="let c of recentMenuConversations; trackBy: trackByConversationId"
                [class.is-current-chat]="c.id === activeConversationId"
                (click)="onSelectConversation(c)">
                <span class="gable-history-menu-title">{{ conversationLabel(c) }}</span>
            </button>
        </ng-container>
        <ng-template #noRecent>
            <div class="gable-history-menu-empty">No recent chats yet</div>
        </ng-template>

        <mat-divider></mat-divider>

        <button
            mat-menu-item
            [matMenuTriggerFor]="archivedMenu"
            [disabled]="archivedConversations.length === 0">
            <i class="ph-bold ph-archive icon-muted icon-16" aria-hidden="true"></i>
            <span>Archived chats</span>
        </button>
    </mat-menu>

    <mat-menu #archivedMenu="matMenu" class="gable-menu-panel gable-history-menu">
        <button
            mat-menu-item
            *ngFor="let c of archivedConversations; trackBy: trackByConversationId"
            (click)="onSelectConversation(c)">
            <span class="gable-history-menu-title">{{ conversationLabel(c) }}</span>
        </button>
    </mat-menu>

    <!-- ============================================================ -->
    <!-- CONTENT ROW — expanded mode puts a history sidebar to the     -->
    <!-- left of the chat column; drawer mode collapses the sidebar.   -->
    <!-- ============================================================ -->
    <div class="gable-content-row">

    <!-- ============================================================ -->
    <!-- SD-6742 — expanded-mode history sidebar (per Figma)          -->
    <!-- ============================================================ -->
    <aside class="gable-sidebar" *ngIf="isExpanded && drawerView !== 'teach'">
        <div class="gable-sidebar-search">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-sidebar-search-field">
                <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                <input
                    matInput
                    type="text"
                    [attr.aria-label]="'Search chats'"
                    placeholder="Search chats"
                    [ngModel]="historySearch"
                    (ngModelChange)="onHistorySearchChange($event)">
                <!-- Spinner lives in the field so it's visible during a search
                     no matter how long the recent list is (the bottom-of-list
                     spinner can scroll out of view). -->
                <i
                    *ngIf="conversationsLoading"
                    matIconSuffix
                    class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin"
                    aria-hidden="true"></i>
            </mat-form-field>
        </div>

        <button type="button" class="gable-sidebar-item gable-sidebar-new" (click)="onNewChat()">
            <i class="ph-bold ph-plus icon-muted icon-16" aria-hidden="true"></i>
            <span>New chat</span>
        </button>

        <div class="gable-sidebar-section-label">Recent chats</div>

        <div class="gable-sidebar-list" (scroll)="onSidebarListScroll($event)">
            <ng-container *ngIf="activeConversations.length > 0; else noRecentSidebar">
                <div
                    class="gable-sidebar-row"
                    *ngFor="let c of activeConversations; trackBy: trackByConversationId"
                    [class.is-active]="c.id === activeConversationId"
                    [class.is-editing]="editingConversationId === c.id">
                    <!-- Inline rename input (expanded mode only — no modal). -->
                    <input
                        *ngIf="editingConversationId === c.id"
                        class="gable-sidebar-rename-input"
                        type="text"
                        maxlength="255"
                        [(ngModel)]="editingTitle"
                        (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                        (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                        (blur)="commitInlineRename(c)"
                        (click)="$event.stopPropagation()"
                        aria-label="Rename chat">
                    <ng-container *ngIf="editingConversationId !== c.id">
                        <button
                            type="button"
                            class="gable-sidebar-item gable-sidebar-chat"
                            (click)="onSelectConversation(c)">
                            <span class="gable-sidebar-chat-title" [innerHTML]="conversationLabel(c) | highlight: historySearch"></span>
                        </button>
                        <button
                            type="button"
                            class="gable-sidebar-overflow"
                            [matMenuTriggerFor]="rowMenu"
                            aria-label="Chat options"
                            (click)="$event.stopPropagation()">
                            <i class="ph-bold ph-dots-three icon-muted icon-16" aria-hidden="true"></i>
                        </button>
                        <mat-menu #rowMenu="matMenu" xPosition="before" class="gable-menu-panel">
                            <button mat-menu-item (click)="onRenameConversation(c)">
                                <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                                <span>Rename</span>
                            </button>
                            <button mat-menu-item (click)="onArchiveConversation(c)">
                                <i class="ph-bold ph-archive icon-muted icon-16" aria-hidden="true"></i>
                                <span>Archive</span>
                            </button>
                        </mat-menu>
                    </ng-container>
                </div>
            </ng-container>

            <!-- Inline footer spinner while a page (search reset / load-more) loads. -->
            <div
                class="gable-sidebar-loading"
                *ngIf="conversationsLoading && activeConversations.length > 0"
                aria-busy="true">
                <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
                <span>Loading…</span>
            </div>

            <ng-template #noRecentSidebar>
                <!-- Suppress the empty copy while the first page is still loading
                     so we don't flash "No recent chats" before results land. -->
                <div class="gable-sidebar-empty" *ngIf="!conversationsLoading">
                    {{ isSearchingHistory ? 'No chats match your search.' : 'No recent chats yet.' }}
                </div>
                <div
                    class="gable-sidebar-loading"
                    *ngIf="conversationsLoading"
                    aria-busy="true">
                    <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
                    <span>Loading…</span>
                </div>
            </ng-template>
        </div>

        <!-- Archived chats pinned to the bottom of the sidebar. -->
        <div class="gable-sidebar-archived">
            <button
                type="button"
                class="gable-sidebar-item gable-sidebar-archived-toggle"
                (click)="toggleShowArchived()">
                <i class="ph-bold ph-archive icon-muted icon-16" aria-hidden="true"></i>
                <span>Archived chats</span>
                <flex-spacer></flex-spacer>
                <i
                    class="ph-bold icon-muted icon-12"
                    [class.ph-caret-down]="!showArchived"
                    [class.ph-caret-up]="showArchived"
                    aria-hidden="true"></i>
            </button>

            <div class="gable-sidebar-list gable-sidebar-archived-list" *ngIf="showArchived">
                <ng-container *ngIf="archivedConversations.length > 0; else noArchivedSidebar">
                    <div
                        class="gable-sidebar-row"
                        *ngFor="let c of archivedConversations; trackBy: trackByConversationId"
                        [class.is-editing]="editingConversationId === c.id">
                        <!-- Inline rename input (expanded mode only — no modal). -->
                        <input
                            *ngIf="editingConversationId === c.id"
                            class="gable-sidebar-rename-input"
                            type="text"
                            maxlength="255"
                            [(ngModel)]="editingTitle"
                            (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                            (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                            (blur)="commitInlineRename(c)"
                            (click)="$event.stopPropagation()"
                            aria-label="Rename chat">
                        <ng-container *ngIf="editingConversationId !== c.id">
                            <button
                                type="button"
                                class="gable-sidebar-item gable-sidebar-chat"
                                (click)="onSelectConversation(c)">
                                <span class="gable-sidebar-chat-title" [innerHTML]="conversationLabel(c) | highlight: historySearch"></span>
                            </button>
                            <button
                                type="button"
                                class="gable-sidebar-overflow"
                                [matMenuTriggerFor]="archivedRowMenu"
                                aria-label="Chat options"
                                (click)="$event.stopPropagation()">
                                <i class="ph-bold ph-dots-three icon-muted icon-16" aria-hidden="true"></i>
                            </button>
                            <mat-menu #archivedRowMenu="matMenu" xPosition="before" class="gable-menu-panel">
                                <button mat-menu-item (click)="onUnarchiveConversation(c)">
                                    <i class="ph-bold ph-arrow-counter-clockwise icon-muted icon-16" aria-hidden="true"></i>
                                    <span>Restore</span>
                                </button>
                                <button mat-menu-item (click)="onRenameConversation(c)">
                                    <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                                    <span>Rename</span>
                                </button>
                            </mat-menu>
                        </ng-container>
                    </div>
                </ng-container>
                <ng-template #noArchivedSidebar>
                    <div class="gable-sidebar-empty">No archived chats.</div>
                </ng-template>
            </div>
        </div>
    </aside>

    <div class="gable-main">

    <!-- ============================================================ -->
    <!-- TEACH GABLE — inline view replaces the chat in drawer mode    -->
    <!-- ============================================================ -->
    <section class="gable-body gable-body-teach" *ngIf="drawerView === 'teach' && teachData as td">
        <app-teach-gable-dialog
            [embedded]="true"
            [embeddedData]="td"
            (closed)="onTeachClosed($event)">
        </app-teach-gable-dialog>
    </section>

    <!-- ============================================================ -->
    <!-- BODY (chat)                                                  -->
    <!-- ============================================================ -->
    <section class="gable-body" *ngIf="drawerView !== 'teach' && activeTab === 'chat'" #conversationScroller (scroll)="onScroll()">

        <!-- LOADING SKELETON — shown while opening a past conversation so the
             switch feels instant and chat-shaped placeholders fill the gap. -->
        <ng-container *ngIf="historyLoading">
            <div class="gable-skeleton" aria-label="Loading conversation" aria-busy="true">
                <div class="gable-skeleton-msg gable-skeleton-msg--user">
                    <span class="gable-skeleton-bubble gable-skeleton-bubble--sm"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--assistant">
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--user">
                    <span class="gable-skeleton-bubble gable-skeleton-bubble--xs"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--assistant">
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                </div>
            </div>
        </ng-container>

        <!-- EMPTY STATE -->
        <ng-container *ngIf="isEmptyState && !historyLoading">
            <div class="gable-empty-state" [class.is-hero]="autopilotUi">
                <app-ai-gable-mascot #emptyMascot class="gable-empty-mascot" [size]="40"></app-ai-gable-mascot>

                <!-- Autopilot preview hero — "I'm in your space, ready to help".
                     Capability cards send REAL prompts through the live chat;
                     the status strip deep-links to the (mock) Activity tab. -->
                <ng-container *ngIf="autopilotUi; else classicGreeting">
                    <h3 class="gable-greeting gable-hero-greeting">{{ heroGreeting }}</h3>
                    <p class="gable-hero-tagline">{{ apHero.tagline }}</p>

                    <!-- Real counts: pending proposals + decided history. -->
                    <div class="gable-hero-strip" *ngIf="showHeroStrip">
                        <span class="gable-hero-strip-icon" aria-hidden="true">
                            <i class="ph-bold ph-lightning icon-16"></i>
                        </span>
                        <span class="gable-hero-strip-text">
                            <span class="gable-hero-strip-title">{{ heroStripTitle }}</span>
                            <span class="gable-hero-strip-desc">{{ heroStripDesc }}</span>
                        </span>
                        <button
                            type="button"
                            class="gable-hero-strip-cta"
                            (click)="onSelectTab('activity')">
                            {{ heroStripCta }}
                        </button>
                    </div>

                    <!-- Module quick links into the Activity tab (per Catherine
                         2026-07-11) — one per module with actual activity;
                         modules with nothing to show render no card. -->
                    <div class="gable-cap-grid" *ngIf="moduleLinks.length > 0">
                        <button
                            *ngFor="let link of moduleLinks; trackBy: trackByModuleLink"
                            type="button"
                            class="gable-cap-card"
                            (click)="onSelectTab('activity', link.module)">
                            <span class="gable-cap-icon" [ngClass]="'tone-' + link.tone" aria-hidden="true">
                                <i class="ph-bold icon-16" [ngClass]="link.icon"></i>
                            </span>
                            <span class="gable-cap-text">
                                <span class="gable-cap-title">{{ link.label }}</span>
                                <span class="gable-cap-desc">{{ link.description }}</span>
                            </span>
                        </button>
                    </div>
                </ng-container>
                <ng-template #classicGreeting>
                    <h3 class="gable-greeting">Hi, I'm Gable! How can I help…</h3>
                </ng-template>

                <!-- Page-aware suggestions only — no topic chips, no section
                     header (per Catherine 2026-07-11). -->
                <ng-container *ngIf="currentSuggestions.length > 0">
                    <ul class="gable-suggestion-list">
                        <li
                            *ngFor="let suggestion of currentSuggestions; trackBy: trackBySuggestion"
                            class="gable-suggestion-row">
                            <button
                                type="button"
                                class="gable-suggestion-btn"
                                (click)="onSuggestionClick(suggestion)">
                                <i class="ph-bold ph-arrow-bend-down-right gable-suggestion-icon icon-muted icon-12" aria-hidden="true"></i>
                                <span class="gable-suggestion-label">{{ suggestion.label }}</span>
                            </button>
                        </li>
                    </ul>
                </ng-container>
            </div>
        </ng-container>

        <!-- CONVERSATION STATE -->
        <ng-container *ngIf="!isEmptyState && !historyLoading">
            <div class="gable-messages" [appGableMessageHighlight]="historySearch">
                <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByIndex">
                    <!-- USER -->
                    <div class="message user-message"
                         *ngIf="msg.role === 'user'"
                         [attr.data-user-msg-index]="i">
                        <div class="message-bubble">{{ msg.content }}</div>
                    </div>

                    <!-- ASSISTANT -->
                    <div class="message assistant-message" *ngIf="msg.role === 'assistant'">
                        <!-- Sandbox deliverable still generating (SD-7240) —
                             skeleton variant of the deliverable card previews
                             exactly what's coming. -->
                        <app-gable-deliverable-card
                            *ngIf="msg.sandboxState === 'generating'"
                            [loading]="true"
                            [loadingLabel]="msg.sandboxLabel || 'Building the document…'"
                            loadingHint="Usually takes a minute or two">
                        </app-gable-deliverable-card>

                        <app-alert-block
                            *ngIf="!msg.sandboxState && msg.alert"
                            [type]="msg.alert">
                            {{ msg.content }}
                        </app-alert-block>

                        <ng-container *ngIf="!msg.sandboxState && !msg.alert">
                            <ng-container
                                *ngIf="(msg.thoughts && msg.thoughts.length > 0) || !!msg.thinkingContent; else loneGable">
                                <app-ai-thought-trail
                                    class="thought-trail-block"
                                    [thoughts]="msg.thoughts || []"
                                    [thinkingContent]="msg.thinkingContent ?? null"
                                    [streaming]="msg.streamState === 'streaming'"
                                    [elapsedSeconds]="elapsedSeconds(msg)"
                                    [collapsed]="msg.thoughtsCollapsed === true"
                                    (collapsedChange)="onThoughtsCollapsedChange(msg.messageId, $event)">
                                    <app-ai-gable-mascot [size]="24"></app-ai-gable-mascot>
                                </app-ai-thought-trail>
                            </ng-container>
                            <ng-template #loneGable>
                                <!-- Sandbox deliverable card rows sit directly
                                     under the turn's text bubble — repeating
                                     the mascot there reads as a second
                                     assistant turn, so suppress it. -->
                                <app-ai-gable-mascot
                                    *ngIf="!msg.sandboxId"
                                    class="assistant-mascot"
                                    [size]="24"></app-ai-gable-mascot>
                            </ng-template>

                            <!-- gable-suggestions fenced blocks are split out of the
                                 markdown and rendered as rich decision cards below
                                 the prose (raw JSON never shows; partial blocks are
                                 hidden until the closing fence streams in). -->
                            <ng-container *ngIf="msg.content | gableSuggestions as sg">
                                <div class="bubble-row" [class.is-streaming]="msg.streamState === 'streaming'">
                                    <div
                                        class="message-bubble"
                                        [class.is-streaming]="msg.streamState === 'streaming'"
                                        [class.is-failed]="msg.streamState === 'failed'"
                                        [attr.aria-live]="msg.streamState === 'streaming' ? 'polite' : null">
                                        <span [innerHTML]="sg.text | markdown: (msg.streamState === 'streaming' ? 'streaming' : 'final')"></span>
                                    </div>
                                </div>

                                <app-gable-suggestion-card
                                    *ngFor="let card of sg.cards; trackBy: trackBySuggestionCard"
                                    [payload]="card"
                                    (actionPrompt)="onSuggestionActionPrompt($event)">
                                </app-gable-suggestion-card>
                            </ng-container>

                            <!-- One "still working" heartbeat for the whole turn,
                                 pinned at the bottom. Runs while streaming until the
                                 answer starts rendering real results (a table / list) —
                                 so thought lines, preamble prose, and quiet
                                 tool-call gaps all read as one continuous live
                                 stream instead of a split thought/answer block. -->
                            <div
                                *ngIf="msg.streamState === 'streaming' && !isRenderingResults(msg.content)"
                                class="gable-working-dots"
                                aria-label="Gable is working">
                                <span></span><span></span><span></span>
                            </div>

                            <div class="citations" *ngIf="msg.citations && msg.citations.length > 0">
                                <span class="citations-label">Citations</span>
                                <span
                                    *ngFor="let cite of msg.citations; trackBy: trackByCitation"
                                    class="citation-pill">
                                    {{ cite.table }}#{{ cite.id }}
                                </span>
                            </div>

                            <ng-container *ngIf="msg.toolCards && msg.toolCards.length > 0">
                                <ng-container *ngFor="let card of msg.toolCards; trackBy: trackByToolCard">
                                    <app-gable-report-card
                                        *ngIf="asReportCardPayload(card) as reportPayload"
                                        [payload]="reportPayload">
                                    </app-gable-report-card>
                                    <app-gable-budget-redirect-card
                                        *ngIf="asBudgetRedirectPayload(card) as budgetPayload"
                                        [payload]="budgetPayload">
                                    </app-gable-budget-redirect-card>
                                    <app-gable-action-preview-card
                                        *ngIf="asActionPreviewPayload(card) as previewPayload"
                                        [payload]="previewPayload"
                                        (confirm)="onActionPreviewConfirm($event)"
                                        (cancel)="onActionPreviewCancel($event)"
                                        (utilityAction)="onActionPreviewUtility($event)">
                                    </app-gable-action-preview-card>
                                    <app-gable-deliverable-card
                                        *ngIf="asDeliverableCardPayload(card) as deliverablePayload"
                                        [payload]="deliverablePayload">
                                    </app-gable-deliverable-card>
                                </ng-container>
                            </ng-container>

            <!-- Feedback-submitted confirmation — replaces the thumbs row once
                 the user has picked a chip + Send (or Teach). Locks further
                 feedback on this interaction. -->
            <app-alert-block
                *ngIf="msg.streamState === 'complete' && msg.interactionId && wasFeedbackSent(msg) && !hasPendingSandboxCard"
                type="success"
                class="feedback-submitted-alert">
                <i class="ph-bold ph-check-circle icon-success icon-16" aria-hidden="true"></i>
                Thanks. Your feedback has been submitted to the team.
            </app-alert-block>

                            <div class="msg-footer"
                                 *ngIf="msg.streamState === 'complete' && msg.interactionId && !wasFeedbackSent(msg) && !hasPendingSandboxCard">
                                <button
                                    type="button"
                                    class="msg-footer-btn"
                                    [class.active]="getFeedback(msg.messageId) === 'good'"
                                    (click)="onFeedback(msg, 'good')"
                                    aria-label="Helpful">
                                    <i class="ph-bold ph-thumbs-up icon-muted icon-16" aria-hidden="true"></i>
                                </button>
                                <button
                                    type="button"
                                    class="msg-footer-btn"
                                    [class.active]="getFeedback(msg.messageId) === 'bad'"
                                    (click)="onFeedback(msg, 'bad')"
                                    title="Report issue"
                                    aria-label="Report issue">
                                    <i class="ph-bold ph-thumbs-down icon-muted icon-16" aria-hidden="true"></i>
                                </button>

                                <!-- "Teach Gable from this answer" — capture point when the
                                     answer landed right. Trigger lives on the conversation's
                                     FIRST user prompt (the original question), guidance
                                     pre-fills with this answer. Hidden once already-taught. -->
                                <button
                                    *ngIf="!wasTaught(msg)"
                                    type="button"
                                    class="msg-footer-btn teach-btn"
                                    (click)="onTeachFromThisAnswer(msg)"
                                    title="Turn this answer into a Gable lesson"
                                    aria-label="Teach Gable from this answer">
                                    <i class="ph-bold ph-graduation-cap icon-ai-purple icon-14" aria-hidden="true"></i>
                                    <span class="teach-btn-label">Teach</span>
                                </button>

                                <!-- "🎓 You taught Gable from this answer" — persistent badge per design spec.
                                     Set by Teach drawer save; tracked in GableDrawerService via localStorage. -->
                                <span class="taught-badge" *ngIf="wasTaught(msg)">
                                    <i class="ph-bold ph-graduation-cap icon-ai-purple icon-12" aria-hidden="true"></i>
                                    You taught Gable from this answer
                                </span>

                                <!-- "Applied a domain note · {title}" used to render here
                                     for every fired lesson — useful for super-admins debugging
                                     retrieval, but homeowners and PMC admins don't need to know
                                     a lesson was consulted to answer their question. The badge
                                     style is reusable elsewhere (see `.applied-lesson-chip` in
                                     the SCSS) — keep the styles around for future surfaces. -->
                            </div>

                            <!-- Inline reason-chips feedback panel — slides in below the thumbs row.
                                 Per design spec: lesson-worthy chips (wrong_data / missed_nuance) +
                                 super user → Teach/Flag/Skip CTAs; otherwise → Send feedback. -->
                            <app-gable-feedback-panel
                                *ngIf="msg.interactionId !== undefined && !wasFeedbackSent(msg) && !hasPendingSandboxCard"
                                [open]="isFeedbackPanelOpenFor(msg.messageId)"
                                [isSuperUser]="isSuperUser"
                                (chipSelected)="onChipFeedback(msg, $event)"
                                (teachRequested)="onTeachRequestedFromChip(msg, $event)"
                                (flagRequested)="onFlagRequestedFromChip(msg, $event)"
                                (skipped)="closeFeedbackPanel()">
                            </app-gable-feedback-panel>

                            <!-- AI-suggested follow-up prompts. Rendered
                                 only under the most recent assistant
                                 bubble so old follow-ups don't pile up. -->
                            <ul class="gable-followup-list"
                                *ngIf="isLastAssistantMessage(i) && apiSuggestions.length > 0">
                                <li
                                    *ngFor="let suggestion of apiSuggestions; trackBy: trackByApiSuggestion"
                                    class="gable-followup-row">
                                    <button
                                        type="button"
                                        class="gable-followup-btn"
                                        [disabled]="isSending"
                                        (click)="onApiSuggestionClick(suggestion)">
                                        <i class="ph-bold ph-arrow-bend-down-right gable-followup-icon icon-muted icon-12" aria-hidden="true"></i>
                                        <span class="gable-followup-label">{{ suggestion }}</span>
                                    </button>
                                </li>
                            </ul>
                        </ng-container>
                    </div>
                </ng-container>

                <!-- Pre-stream thinking placeholder -->
                <div
                    class="message assistant-message"
                    *ngIf="isSending && !hasStreamingAssistant && (!pendingQuestions || submittingAnswers)">
                    <div class="thinking-bubble">
                        <app-ai-gable-mascot [size]="24"></app-ai-gable-mascot>
                        <span>Thinking…</span>
                    </div>
                </div>

                <!-- Question card -->
                <app-question-card
                    *ngIf="pendingQuestions && !submittingAnswers"
                    [batch]="pendingQuestions"
                    [submitting]="submittingAnswers"
                    (submitAnswers)="onAnswers($event)"
                    (close)="onCloseQuestionCard()">
                </app-question-card>
            </div>

        </ng-container>
    </section>

    <!-- ============================================================ -->
    <!-- Activity — the review queue + feed (mock preview; SD-7969     -->
    <!-- wires it to pending proposals + the commit log).              -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        *ngIf="autopilotUi && drawerView !== 'teach' && activeTab === 'activity'">
        <app-gable-activity-panel [initialFilter]="activityFilter"></app-gable-activity-panel>
    </section>

    <!-- ============================================================ -->
    <!-- FOOTER / COMPOSER                                            -->
    <!-- ============================================================ -->
    <footer class="gable-footer" *ngIf="!pendingQuestions && drawerView !== 'teach' && activeTab === 'chat'">
        <div class="gable-composer">
            <textarea
                #messageInput
                class="gable-composer-input"
                rows="1"
                [placeholder]="'Ask about ' + orgName + '…'"
                [ngModel]="draftMessage"
                (ngModelChange)="onDraftChange($event)"
                (keydown)="onKeydown($event)"
                [disabled]="isSending"></textarea>
            <div class="gable-composer-actions">
                <flex-spacer></flex-spacer>
                <!-- Submit toggles into a Stop button while a stream is
                     in flight, mirroring Claude desktop. Clicking it
                     cancels the in-flight assistant turn. -->
                <button
                    *ngIf="!isSending"
                    type="button"
                    class="gable-send-btn"
                    [disabled]="!draftMessage.trim()"
                    (click)="onSend()"
                    aria-label="Send message">
                    <i class="ph-bold ph-arrow-up icon-16" aria-hidden="true"></i>
                </button>
                <button
                    *ngIf="isSending"
                    type="button"
                    class="gable-send-btn"
                    (click)="onStopStream()"
                    aria-label="Stop response">
                    <i class="ph-bold ph-stop-circle icon-16" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </footer>

    </div><!-- /.gable-main -->
    </div><!-- /.gable-content-row -->
</div>
